import { Input } from '@affine/component';
import { useI18n } from '@affine/i18n';
import type { Store } from '@blocksuite/store';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { resolveRelationEndpointAccess } from './access-state';
import { databaseExists, type RowInfo } from './database-discovery';
import {
  databaseAccessRevision,
  getCrossDocDatabaseRowsWindow,
  isCrossDocRelationServiceInitialized,
  setCrossDocRowDisplayCached,
} from './database-discovery-bridge';
import * as styles from './dropdown.css';
import { waitForDatabaseRowsWindow } from './row-projection';

const RELATION_DROPDOWN_PAGE_SIZE = 50;

export interface RelationDropdownProps {
  store: Store;
  targetDatabaseId: string;
  targetDocId?: string; // Cross-doc support
  targetFieldId: string;
  cardinality: 'one' | 'many';
  currentValue: string[];
  excludeRowId?: string; // Fuer Self-Relations
  onChange: (newValue: string[]) => void;
  onClose: () => void;
}

/**
 * Dropdown fuer die Auswahl von Related Rows
 * Supports both same-doc and cross-doc relations.
 */
export function RelationDropdown({
  store,
  targetDatabaseId,
  targetDocId: configuredTargetDocId,
  targetFieldId,
  cardinality,
  currentValue,
  excludeRowId,
  onChange,
  onClose,
}: RelationDropdownProps) {
  const t = useI18n();
  const accessRevision = useSyncExternalStore(
    useCallback(callback => databaseAccessRevision.subscribe(callback), []),
    () => databaseAccessRevision.value
  );
  // Resolved every render: accessRevision re-renders on access changes and the
  // lookup reads the current discovery state.
  const endpointAccess = resolveRelationEndpointAccess({
    targetDatabaseId,
    targetDocId: configuredTargetDocId,
  });
  const targetDocId = endpointAccess.targetDocId;
  const isCrossDoc = !!targetDocId && targetDocId !== store.id;
  const canEditTarget = endpointAccess.state === 'write';
  const [searchQuery, setSearchQuery] = useState('');
  const [searchText, setSearchText] = useState('');
  const requestRevision = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setSearchText(searchQuery.trim()), 150);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const [allRows, setAllRows] = useState<RowInfo[]>([]);
  const [hasMoreRows, setHasMoreRows] = useState(false);
  const [totalRowCount, setTotalRowCount] = useState(0);
  // Start with loading=true for cross-doc to show "Loading..." immediately
  const [isLoading, setIsLoading] = useState(
    !!(isCrossDoc && isCrossDocRelationServiceInitialized())
  );

  const cacheCrossDocRows = useCallback(
    (rows: RowInfo[]) => {
      if (!targetDocId) {
        return;
      }
      for (const row of rows) {
        setCrossDocRowDisplayCached(
          targetDocId,
          targetDatabaseId,
          row.id,
          row.display
        );
      }
    },
    [targetDatabaseId, targetDocId]
  );

  const loadRowsWindow = useCallback(
    async (offset: number) => {
      const request = {
        offset,
        limit: RELATION_DROPDOWN_PAGE_SIZE,
        searchText,
        signal: activeRequest.current?.signal,
      };

      if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
        const rowsWindow = await getCrossDocDatabaseRowsWindow(
          targetDocId!,
          targetDatabaseId,
          targetFieldId,
          request
        );
        return rowsWindow;
      }

      return waitForDatabaseRowsWindow(
        store,
        targetDatabaseId,
        targetFieldId,
        request
      );
    },
    [
      searchText,
      isCrossDoc,
      store,
      targetDatabaseId,
      targetDocId,
      targetFieldId,
    ]
  );

  // Load rows - supports both local and cross-doc
  useEffect(() => {
    const revisionCounter = requestRevision;
    const revision = ++revisionCounter.current;
    const controller = new AbortController();
    activeRequest.current = controller;
    if (!targetDatabaseId || !targetFieldId || !canEditTarget) {
      setAllRows([]);
      setHasMoreRows(false);
      setTotalRowCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    loadRowsWindow(0)
      .then(rowsWindow => {
        if (
          requestRevision.current !== revision ||
          databaseAccessRevision.value !== accessRevision
        )
          return;
        if (isCrossDoc) cacheCrossDocRows(rowsWindow.rows);
        setAllRows(rowsWindow.rows);
        setHasMoreRows(rowsWindow.hasMore);
        setTotalRowCount(rowsWindow.totalCount);
      })
      .catch(e => {
        if (requestRevision.current !== revision) return;
        console.warn('[RelationDropdown] Failed to load rows:', e);
        setAllRows([]);
        setHasMoreRows(false);
        setTotalRowCount(0);
      })
      .finally(() => {
        if (requestRevision.current === revision) setIsLoading(false);
      });
    return () => {
      revisionCounter.current++;
      controller.abort();
    };
  }, [
    canEditTarget,
    accessRevision,
    targetDatabaseId,
    targetFieldId,
    loadRowsWindow,
    isCrossDoc,
    cacheCrossDocRows,
  ]);

  const handleLoadMore = useCallback(() => {
    if (isLoading) {
      return;
    }

    const revision = requestRevision.current;
    setIsLoading(true);
    loadRowsWindow(allRows.length)
      .then(rowsWindow => {
        if (
          requestRevision.current !== revision ||
          databaseAccessRevision.value !== accessRevision
        )
          return;
        if (isCrossDoc) cacheCrossDocRows(rowsWindow.rows);
        setAllRows(rows => [...rows, ...rowsWindow.rows]);
        setHasMoreRows(rowsWindow.hasMore);
        setTotalRowCount(rowsWindow.totalCount);
      })
      .catch(e => {
        console.warn('[RelationDropdown] Failed to load more rows:', e);
      })
      .finally(() => {
        if (requestRevision.current === revision) setIsLoading(false);
      });
  }, [
    allRows.length,
    isLoading,
    loadRowsWindow,
    accessRevision,
    isCrossDoc,
    cacheCrossDocRows,
  ]);

  // Filtere Rows: Self-Relation ausschliessen + Suche
  const filteredRows = useMemo(() => {
    let rows = allRows;

    // Self-Relation: Eigene Row ausfiltern
    if (excludeRowId) {
      rows = rows.filter(row => row.id !== excludeRowId);
    }

    return rows;
  }, [allRows, excludeRowId]);

  const isSelected = useCallback(
    (rowId: string) => currentValue.includes(rowId),
    [currentValue]
  );

  const handleSelect = useCallback(
    (rowId: string) => {
      if (!canEditTarget) {
        return;
      }
      if (cardinality === 'one') {
        // Radio-Style: a new row replaces the link, the linked row unlinks.
        onChange(isSelected(rowId) ? [] : [rowId]);
        onClose();
      } else {
        // Checkbox-Style: Toggle
        const newValue = isSelected(rowId)
          ? currentValue.filter(id => id !== rowId)
          : [...currentValue, rowId];
        onChange(newValue);
      }
    },
    [canEditTarget, cardinality, currentValue, isSelected, onChange, onClose]
  );

  const handleClear = useCallback(() => {
    if (!canEditTarget) {
      return;
    }
    onChange([]);
    onClose();
  }, [canEditTarget, onChange, onClose]);

  // Konfiguration nicht vollstaendig
  if (!targetDatabaseId || !targetFieldId) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <span className={styles.warningIcon}>!</span>
          <span>
            {t['com.streamlain.streamlain.relation.configure-target-first']()}
          </span>
        </div>
      </div>
    );
  }

  // E018-S015-T001: Check if local target database was deleted
  const isLocalTargetDeleted =
    !isCrossDoc && !databaseExists(store, targetDatabaseId);

  if (isLocalTargetDeleted) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <span className={styles.warningIcon}>⚠️</span>
          <span>
            {t['com.streamlain.streamlain.relation.target-deleted']()}
          </span>
        </div>
      </div>
    );
  }

  if (!canEditTarget) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <span className={styles.warningIcon}>!</span>
          <span>
            {endpointAccess.state === 'restricted'
              ? t['com.streamlain.streamlain.relation.target-restricted']()
              : t['com.streamlain.streamlain.relation.target-read-only']()}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Search Input */}
      <div className={styles.searchContainer}>
        <Input
          value={searchQuery}
          onChange={value => setSearchQuery(value)}
          placeholder={t['com.streamlain.streamlain.relation.search-rows']()}
          className={styles.searchInput}
          autoFocus
        />
      </div>

      {/* Rows List */}
      <div className={styles.rowsList}>
        {isLoading ? (
          <div className={styles.emptyState}>
            {t['com.streamlain.streamlain.relation.loading-rows']()}
          </div>
        ) : filteredRows.length === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery
              ? t['com.streamlain.streamlain.relation.no-matching-rows']()
              : t['com.streamlain.streamlain.relation.no-rows']()}
          </div>
        ) : (
          <>
            {filteredRows.map(row => (
              <RowItem
                key={row.id}
                row={row}
                selected={isSelected(row.id)}
                cardinality={cardinality}
                onSelect={() => handleSelect(row.id)}
              />
            ))}
            {hasMoreRows ? (
              <button
                type="button"
                className={styles.loadMoreButton}
                onClick={handleLoadMore}
                disabled={isLoading}
              >
                {t['com.streamlain.streamlain.relation.load-more']({
                  loaded: String(allRows.length),
                  total: String(totalRowCount),
                })}
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* Footer */}
      {currentValue.length > 0 && (
        <div className={styles.footer}>
          <button className={styles.clearButton} onClick={handleClear}>
            {t['com.streamlain.streamlain.relation.clear-selection']()}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Single Row Item
 */
function RowItem({
  row,
  selected,
  cardinality,
  onSelect,
}: {
  row: RowInfo;
  selected: boolean;
  cardinality: 'one' | 'many';
  onSelect: () => void;
}) {
  return (
    <div
      className={`${styles.rowItem} ${selected ? styles.rowItemSelected : ''}`}
      onClick={onSelect}
    >
      <span className={styles.checkbox}>
        {cardinality === 'one' ? (selected ? '●' : '○') : selected ? '☑' : '☐'}
      </span>
      <span className={styles.rowDisplay}>{row.display}</span>
    </div>
  );
}
